import { VisibilityState } from '@tanstack/react-table';

import { Doc } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { PopoverContent } from '@/new/components/shadcn/popover';

interface ColumnConfigPopoverProps {
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState) => void;
  onClose?: () => void;
  docs?: Doc[];
}

const STANDARD_COLUMNS = [
  // Checked by default
  {
    id: 'type',
    label: 'Type',
  },
  {
    id: 'file.extension',
    label: 'Extension',
  },
  {
    id: 'issues',
    label: 'Issues',
  },
  {
    id: 'persons',
    label: 'Entities',
  },
  {
    id: 'date',
    label: 'Document Date',
  },
  // Unchecked by default
  {
    id: 'author',
    label: 'Author',
  },
  {
    id: 'factsCount',
    label: 'Facts Count',
  },
  {
    id: 'file.createdDate',
    label: 'Run Date/Time',
  },
];

const RELATIVITY_COLUMNS = [
  {
    id: 'file.relativityMetadata.controlNumber',
    label: 'Control Number',
  },
  {
    id: 'file.relativityMetadata.issues',
    label: 'Relativity Issues',
  },
];

export function ColumnConfigPopover({
  columnVisibility,
  setColumnVisibility,
  onClose,
  docs,
}: ColumnConfigPopoverProps) {
  const handleToggleColumn = (columnId: string) => {
    setColumnVisibility({
      ...columnVisibility,
      // Explicitly set true (show) or false (hide)
      // undefined = visible by default, so if currently visible (undefined or true), hide it
      [columnId]: columnVisibility[columnId] === false ? true : false,
    });
  };

  // Check if any documents have relativity metadata
  const hasRelativityMetadata = docs?.some((doc) => doc.file?.relativityMetadata) ?? false;

  return (
    <PopoverContent side="left" className="mt-12 w-96" sideOffset={8}>
      <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4">
        <div className="flex items-center gap-2">
          <Icon className="lucide--columns-3 h-4 w-4 text-gray-600" />
          <span className="font-medium text-gray-700">Edit Columns</span>
        </div>
      </div>

      <div className="mt-4 space-y-4">
        <div>
          <div className="space-y-2.5">
            {STANDARD_COLUMNS.map((column) => (
              <label key={column.id} htmlFor={column.id} className="flex cursor-pointer items-center gap-2.5">
                <Checkbox
                  id={column.id}
                  checked={columnVisibility[column.id] !== false}
                  onCheckedChange={() => handleToggleColumn(column.id)}
                />
                <span className="text-sm font-medium text-gray-700">{column.label}</span>
              </label>
            ))}
          </div>
        </div>

        {hasRelativityMetadata && (
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Icon className="lucide--database h-4 w-4 text-purple-600" />
              <h4 className="text-sm font-medium text-purple-700">Relativity Metadata</h4>
            </div>
            <div className="space-y-2.5">
              {RELATIVITY_COLUMNS.map((column) => (
                <label key={column.id} htmlFor={column.id} className="flex cursor-pointer items-center gap-2.5">
                  <Checkbox
                    id={column.id}
                    checked={columnVisibility[column.id] !== false}
                    onCheckedChange={() => handleToggleColumn(column.id)}
                  />
                  <span className="text-sm font-medium text-gray-700">{column.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex w-full justify-end border-t pt-4">
        <Button onClick={onClose} size="sm">
          <Icon className="lucide--check h-3.5 w-3.5" />
          <span>Done</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
